<template>
  <div
    class="settings-view-container m-4"
    :class="[
      !settingData.pagination ? 'no-pagination' : '',
      !settingData.search ? 'no-search' : ''
    ]"
  >
    <div
      class="d-flex flex-row justify-content-between align-items-center mb-3"
    >
      <h5 class="mb-0">{{ settingData.title }}</h5>
      <b-button class="pt-2 pb-2" size="sm" @click="addRow" variant="primary"
        >New</b-button
      >
    </div>
    <div
      v-if="settingData.search"
      class="d-flex flex-row justify-content-end align-items-center mb-3"
    >
      <b-form-group class="text-left mb-0">
        <b-form-input
          size="sm"
          placeholder="Search"
          v-model="search"
        ></b-form-input>
      </b-form-group>
    </div>
    <grid
      :groupFeature="false"
      :columns="columns"
      :autoHeight="true"
      :data="controlValueComputed"
      :columnLines="gridConfig.columnLines"
      :searchFeature="true"
      :rowReorderFeature="true"
      @storesort="reorder"
      :stripeFeature="false"
    ></grid>
    <bottom-paginator
      v-if="settingData.pagination"
      :total="total"
      :page="paginator.page"
      :perPage="paginator.perPage"
      @change-page="changePage"
    />
    <b-modal
      centered
      ref="form-modal"
      :title="(formId ? 'Edit ' : 'Add ') + settingData.singularTitle"
      header-bg-variant="primary"
      header-text-variant="white"
      no-close-on-backdrop
      header-class="bg-purple"
    >
      <template v-slot:modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button variant="primary" @click="submitData">
          Save
        </b-button>
      </template>
      <b-form-group label="Name">
        <b-form-input
          v-model="form.name"
          :maxLength="
            settingData.nameMaxLength ? settingData.nameMaxLength : null
          "
        ></b-form-input>
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import Grid from "../../third_party/grid/Grid";
import settingCrud from "../../server/genericSettingCrud";
import BottomPaginator from "../controls/BottomPaginator.vue";
import { mapActions } from "vuex";

export default {
  name: "GenericSettingCrud",
  components: { Grid, BottomPaginator },
  props: ["settingData"],
  data() {
    return {
      formId: null,
      form: {
        name: ""
      },
      gridConfig: {
        minHeight: "20em",
        columnLines: false
      },
      columns: [
        {
          text: "Name",
          field: "name",
          width: "90%",
          sortable: false
        },
        {
          text: "Tools",
          type: "action",
          cellCls: "pointer-cell",
          field: "action",
          width: "10%",
          align: "end",
          actions: [
            {
              tooltip: "Edit",
              renderer: ({ record }) =>
                record.readOnly === undefined || record.readOnly === false
                  ? `<img class="b-action-item mr-2" src="${
                      this.isStandalone()
                        ? `/icons/edit.svg`
                        : `/desktop/icons/edit.svg`
                    }">`
                  : "",
              onClick: ({ record }) => {
                this.formId = record.id;
                this.form = {
                  name: record.name
                };
                this.$refs["form-modal"].show();
              }
            },
            {
              tooltip: "Delete",
              renderer: ({ record }) =>
                record.readOnly === undefined || record.readOnly === false
                  ? `<img class="b-action-item" src=	"${
                      this.isStandalone()
                        ? `/icons/delete.svg`
                        : `/desktop/icons/delete.svg`
                    }">`
                  : "",
              onClick: ({ record }) => {
                this.$dialog
                  .confirm(
                    {
                      title: "Are you sure you want to delete this record?"
                    },
                    {
                      view: "confirm-delete"
                    }
                  )
                  .then(() => {
                    this.deleteRow(record.id);
                  })
                  .catch(function() {});
              }
            }
          ]
        }
      ],
      controlValue: [],
      total: 0,
      paginator: {
        page: 0,
        perPage: 10
      },
      search: ""
    };
  },
  mounted() {
    this.loadData();
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    loadData(refetch = false) {
      const data = {};
      if (this.settingData.pagination) {
        data.page = this.paginator.page;
        data.pageSize = this.paginator.perPage;
        settingCrud
          .count(this.settingData.path, {
            searchPhrase: this.search
          })
          .then(data => {
            this.total = data;
          })
          .catch(() => {});
      }
      if (this.settingData.search) {
        data.searchPhrase = this.search;
      }
      settingCrud
        .query(this.settingData.path, data)
        .then(data => {
          this.controlValue = data;
        })
        .catch(() => {});
      if (refetch) {
        this.$store.dispatch("client/" + this.settingData.fetchAction);
      }
    },
    async submitData() {
      let noErrors = true;
      if (this.formId) {
        try {
          await settingCrud.update(this.settingData.path, this.formId, {
            name: this.form.name
          });
        } catch (e) {
          noErrors = false;
          this.showError({
            message: "Name already exists."
          });
        }
      } else {
        try {
          await settingCrud.create(this.settingData.path, {
            name: this.form.name
          });
        } catch (e) {
          noErrors = false;
          this.showError({
            message: "Name already exists."
          });
        }
      }
      if (!noErrors) return;
      this.showAlertSuccess();
      this.loadData(true);
      this.$refs["form-modal"].hide();
    },
    addRow() {
      this.formId = null;
      this.form = {
        name: ""
      };
      this.$refs["form-modal"].show();
    },
    deleteRow(id) {
      settingCrud
        .delete(this.settingData.path, id)
        .then(() => {
          this.loadData(true);
        })
        .catch(() => {});
    },
    async reorder(event) {
      this.paginator.page = 0;
      const newOrder = event.records.map(rec => rec.data.id);
      await settingCrud.reorder(this.settingData.path, newOrder);
      this.loadData(true);
    },
    changePage(page) {
      this.paginator.page = page;
      this.loadData();
    }
  },
  computed: {
    controlValueComputed() {
      if (!this.settingData.pagination && this.settingData.search)
        return this.controlValue.filter(c =>
          c.name.toLowerCase().includes(this.search.toLowerCase())
        );
      return this.controlValue;
    }
  },
  watch: {
    search: function() {
      if (this.settingData.pagination) {
        this.paginator = {
          page: 0,
          perPage: 10
        };
        this.loadData();
      }
    }
  }
};
</script>

<style>
.settings-view-container .b-grid-container .b-container {
  max-height: calc(100vh - 364px);
}

.settings-view-container.no-pagination .b-grid-container .b-container {
  max-height: calc(100vh - 334px);
}

.settings-view-container.no-search .b-grid-container .b-container {
  max-height: calc(100vh - 319px);
}

.settings-view-container.no-pagination.no-search
  .b-grid-container
  .b-container {
  max-height: calc(100vh - 281px);
}
</style>
